import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface FeatureInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: {
    description: string;
    benefits: string[];
    example?: string;
  };
}

export function FeatureInfoModal({
  isOpen,
  onClose,
  title,
  content,
}: FeatureInfoModalProps) {
  return (
    <Transition appear
      show={ isOpen }
      as={ Fragment }>
      <Dialog as={ `div` }
        className={ `relative z-50` }
        onClose={ onClose }>
        <Transition.Child
          as={ Fragment }
          enter={ `ease-out duration-300` }
          enterFrom={ `opacity-0` }
          enterTo={ `opacity-100` }
          leave={ `ease-in duration-200` }
          leaveFrom={ `opacity-100` }
          leaveTo={ `opacity-0` }
        >
          <div className={ `fixed inset-0 bg-black/25` } />
        </Transition.Child>

        <div className={ `fixed inset-0 overflow-y-auto` }>
          <div className={ `flex min-h-full items-center justify-center p-4 text-center` }>
            <Transition.Child
              as={ Fragment }
              enter={ `ease-out duration-300` }
              enterFrom={ `opacity-0 scale-95` }
              enterTo={ `opacity-100 scale-100` }
              leave={ `ease-in duration-200` }
              leaveFrom={ `opacity-100 scale-100` }
              leaveTo={ `opacity-0 scale-95` }
            >
              <Dialog.Panel className={ `w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all` }>
                <div className={ `flex justify-between items-start mb-4` }>
                  <Dialog.Title
                    as={ `h3` }
                    className={ `text-xl font-semibold leading-6 text-gray-900` }
                  >
                    { title }
                  </Dialog.Title>
                  <button
                    type={ `button` }
                    className={ `rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden` }
                    onClick={ onClose }
                  >
                    <XMarkIcon className={ `h-6 w-6` } />
                  </button>
                </div>

                <div className={ `mt-4 space-y-4` }>
                  <p className={ `text-sm text-gray-600 leading-relaxed` }>
                    { content.description }
                  </p>

                  <div>
                    <h4 className={ `text-sm font-semibold text-gray-900 mb-2` }>
                      { `Key Benefits:` }
                    </h4>
                    <ul className={ `list-disc list-inside space-y-1 text-sm text-gray-600` }>
                      { content.benefits.map((benefit, idx) => (
                        <li key={ idx }>{ benefit }</li>
                      )) }
                    </ul>
                  </div>

                  { content.example && (
                    <div className={ `bg-gray-50 p-4 rounded-lg` }>
                      <h4 className={ `text-sm font-semibold text-gray-900 mb-2` }>
                        { `Example Use Case:` }
                      </h4>
                      <p className={ `text-sm text-gray-600 leading-relaxed` }>
                        { content.example }
                      </p>
                    </div>
                  ) }
                </div>

                <div className={ `mt-6 flex justify-end gap-3` }>
                  <button
                    type={ `button` }
                    className={ `inline-flex justify-center rounded-md border border-transparent bg-paidniceGreen-600 px-4 py-2 text-sm font-medium text-white hover:bg-paidniceGreen-700 focus:outline-hidden` }
                    onClick={ onClose }
                  >
                    { `Got it!` }
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
